import { useQuery } from '@tanstack/react-query'
import {
  Button,
  DatePicker,
  Divider,
  Flex,
  Select,
  Tag,
  Tooltip,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import type { TFunction } from 'i18next'
import { Download, RotateCw } from 'lucide-react'
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  ActorTypeEnum,
  SeverityEnum,
  organizationsAuditLogsListOptions,
  type AuditLogList,
  type OrganizationsAuditLogsListData
} from 'src/api/main'
import {
  ActiveFilterTags,
  BadgeText,
  AuditActionTag,
  DateTimeCell,
  FilterSelect,
  MoreFilters,
  ProTableContainer,
  QueryErrorAlert,
  SearchInput,
  SeverityDot,
  UserCell
} from 'src/components'
import { FILTER_CONTROL_WIDTH } from 'src/constants'
import { FilterLabelsProvider } from 'src/context'
import { AuditExportModal } from 'src/features/workspaces/components'
import {
  useActorFilter,
  useFacetFilter,
  useQueryParams,
  useQueryUpdate,
  useHasWorkspacePermissions
} from 'src/hooks'
import { paths } from 'src/router'
import {
  SEVERITY_COLORS,
  SEVERITY_TAG_COLOR,
  defaultFilters,
  getActorlessLabel,
  getAuditActorName,
  getAuditEventCode,
  humanizeAuditCode,
  normalizeAuditSeverity,
  toAuditAcceptLanguage,
  toDisplayString,
  translateAuditAction,
  translateAuditActorType,
  translateAuditSeverity,
  WorkspacePermissionCode
} from 'src/utils'
import type { ActiveFilterConfig } from 'src/utils'

const { Text } = Typography
const { RangePicker } = DatePicker

type SeverityFilter = SeverityEnum | 'all'

const TABLE_SCROLL_MARGIN_TOP = 72

type AuditListQuery = NonNullable<OrganizationsAuditLogsListData['query']>

type DecoratedEvent = AuditLogList & {
  createdAt: string
  severityValue: SeverityEnum | null
  actorName: string
  actorEmail?: string
  descriptionLabel: string
  ipAddress?: string | null
}

const getDescriptionLabel = (event: AuditLogList) =>
  event.description?.trim() || humanizeAuditCode(getAuditEventCode(event))

const decorateEvent = (event: AuditLogList, t: TFunction): DecoratedEvent => ({
  ...event,
  createdAt: event.timestamp,
  severityValue: normalizeAuditSeverity(event.severity),
  // No actor means no principal, and the event already says which kind that was —
  // so the label comes from the data (and gets translated) instead of a hardcoded
  // English fallback.
  actorName: getAuditActorName(
    event.actor,
    getActorlessLabel(event.actor_type, t)
  ),
  actorEmail: toDisplayString(event.actor?.email) || undefined,
  descriptionLabel: getDescriptionLabel(event),
  ipAddress: event.client_ip
})

type Props = {
  workspaceId: number
  idValid: boolean
  /** Rendered as the left slot of the toolbar's top row (view switcher). */
  viewSelector: ReactNode
  /** Optional heading rendered in the top-left of the toolbar. */
  header?: ReactNode
}

const EventsPanelBase = ({
  workspaceId,
  idValid,
  viewSelector,
  header
}: Props) => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const language = i18n.resolvedLanguage ?? i18n.language
  const tableTopRef = useRef<HTMLDivElement>(null)
  const [exportOpen, setExportOpen] = useState(false)
  const canExportAudit = useHasWorkspacePermissions(
    WorkspacePermissionCode.AUDIT_EXPORT
  )

  const params = useQueryParams()
  const { updateQuery } = useQueryUpdate()

  const page = Number(params.page) || defaultFilters.page
  const perPage =
    Number(params.page_size ?? params.per_page) || defaultFilters.per_page
  const searchTerm = String(params.search ?? '')
  const actorTypeFilter = String(params.actor_type ?? '')
  const actorIdFilter = String(params.actor_id ?? '')
  const entityIdFilter = String(params.entity_id ?? '')
  const eventTypeFilter = String(params.event_type ?? '')
  const serviceFilter = String(params.service ?? '')
  const severityFilter =
    (params.severity as SeverityFilter | undefined) ?? 'all'
  const fromFilter = String(params.from ?? '')
  const toFilter = String(params.to ?? '')

  const acceptLanguage = toAuditAcceptLanguage(language)

  const auditQuery = useMemo<AuditListQuery>(
    () => ({
      limit: perPage,
      offset: (page - 1) * perPage,
      search: searchTerm || undefined,
      actor_type: actorTypeFilter
        ? (actorTypeFilter as ActorTypeEnum)
        : undefined,
      actor_id: actorIdFilter || undefined,
      entity_id: entityIdFilter || undefined,
      event_type: eventTypeFilter || undefined,
      service: serviceFilter || undefined,
      severity: severityFilter === 'all' ? undefined : severityFilter,
      timestamp_gte: fromFilter || undefined,
      timestamp_lte: toFilter || undefined
    }),
    [
      perPage,
      page,
      searchTerm,
      actorTypeFilter,
      actorIdFilter,
      entityIdFilter,
      eventTypeFilter,
      serviceFilter,
      severityFilter,
      fromFilter,
      toFilter
    ]
  )

  const { data, isLoading, isFetching, isError, refetch } = useQuery({
    ...organizationsAuditLogsListOptions({
      path: { organization_id: workspaceId },
      query: auditQuery,
      headers: { 'Accept-Language': acceptLanguage }
    }),
    enabled: idValid,
    placeholderData: (previousData) => previousData,
    // Opt out of the app-wide refetchOnMount:false. New events land constantly, so
    // re-entering the page must hit the backend instead of rendering the old page.
    refetchOnMount: 'always',
    // Audit rows are append-only and land constantly: also re-fetch when the user
    // comes back to the browser tab, since a mount-only refetch misses the case
    // where the action was performed elsewhere and this page stayed open.
    refetchOnWindowFocus: true
  })

  const rows = useMemo(
    () => (data?.results ?? []).map((event) => decorateEvent(event, t)),
    [data?.results, t]
  )
  const total = data?.count ?? 0

  const actionLabel = useCallback(
    (value: string) => translateAuditAction(t, value),
    [t]
  )
  const eventType = useFacetFilter({
    organizationId: workspaceId,
    field: 'event_type',
    selected: eventTypeFilter,
    getLabel: actionLabel,
    enabled: idValid
  })
  const service = useFacetFilter({
    organizationId: workspaceId,
    field: 'service',
    selected: serviceFilter,
    getLabel: humanizeAuditCode,
    enabled: idValid
  })
  const actorTypeLabel = useCallback(
    (value: string) => translateAuditActorType(t, value),
    [t]
  )
  const actorType = useFacetFilter({
    organizationId: workspaceId,
    field: 'actor_type',
    selected: actorTypeFilter,
    getLabel: actorTypeLabel,
    enabled: idValid
  })
  const actor = useActorFilter(workspaceId, actorIdFilter, rows)
  const rangeValue = useMemo<[Dayjs | null, Dayjs | null] | null>(() => {
    const fromDay = fromFilter ? dayjs(fromFilter) : null
    const toDay = toFilter ? dayjs(toFilter) : null

    return fromDay || toDay ? [fromDay, toDay] : null
  }, [fromFilter, toFilter])

  const filterTagItems: ActiveFilterConfig[] = [
    {
      type: 'select',
      key: 'event_type',
      label: t('workspace.auditPage.filters.allActions')
    },
    {
      type: 'select',
      key: 'actor_id',
      label: t('workspace.auditPage.filters.allActors')
    },
    {
      type: 'select',
      key: 'service',
      label: t('workspace.auditPage.filters.allApplications')
    },
    {
      type: 'select',
      key: 'actor_type',
      label: t('workspace.auditPage.filters.allActorTypes')
    },
    {
      type: 'select',
      key: 'severity',
      label: t('workspace.auditPage.filters.allSeverities')
    },
    {
      type: 'select',
      key: 'entity_id',
      label: t('workspace.auditPage.filters.entityId')
    },
    {
      type: 'dateRange',
      key: 'from',
      from: 'from',
      to: 'to',
      label: t('workspace.auditPage.dateRangeLabel'),
      clearKeys: ['from', 'to']
    }
  ]

  const moreFiltersActiveCount = [
    serviceFilter,
    actorTypeFilter,
    entityIdFilter,
    severityFilter === 'all' ? '' : severityFilter,
    fromFilter || toFilter
  ].filter((value) => value !== '').length

  const setFilter = (patch: Record<string, unknown>) =>
    updateQuery({ ...params, page: defaultFilters.page, ...patch })

  const resetMoreFilters = () =>
    setFilter({
      service: null,
      actor_type: null,
      entity_id: null,
      severity: null,
      from: null,
      to: null
    })

  const clearFilters = () => {
    updateQuery({}, true)
  }

  // The actor dropdown is built from the organization's member directory, so a
  // selected person can only ever be a `user`. Keeping both would silently return
  // nothing; drop the actor rather than leave an empty table with no reason shown.
  const handleActorTypeChange = (value: string) => {
    setFilter({
      actor_type: value || null,
      ...(value && value !== ActorTypeEnum.USER && actorIdFilter
        ? { actor_id: null }
        : {})
    })
  }

  const handleRangeChange = (next: [Dayjs | null, Dayjs | null] | null) => {
    setFilter({
      from: next?.[0] ? next[0].startOf('day').toISOString() : null,
      to: next?.[1] ? next[1].endOf('day').toISOString() : null
    })
  }

  const scrollToTableTop = useCallback(() => {
    requestAnimationFrame(() => {
      tableTopRef.current?.scrollIntoView({
        block: 'start',
        behavior: 'auto'
      })
    })
  }, [])

  const columns: ColumnsType<DecoratedEvent> = useMemo(
    () => [
      {
        title: '',
        key: 'severity_dot',
        fixed: 'left',
        width: 24,
        align: 'center',
        render: (_v, record) => <SeverityDot severity={record.severityValue} />
      },
      {
        title: t('workspace.auditPage.columns.time'),
        dataIndex: 'createdAt',
        key: 'timestamp',
        fixed: 'left',
        width: 150,
        render: (value: string) => (
          <DateTimeCell
            value={value}
            variant="relative"
            style={{ fontSize: 12 }}
          />
        )
      },
      {
        title: t('workspace.auditPage.columns.application'),
        dataIndex: 'application_name',
        key: 'application',
        width: 150,
        render: (name: string | null, record) => (
          <Flex vertical style={{ lineHeight: 1.25 }}>
            <Text strong style={{ fontSize: 12 }}>
              {name || humanizeAuditCode(record.service)}
            </Text>
            <Text type="secondary" style={{ fontSize: 11 }}>
              {record.service}
            </Text>
          </Flex>
        )
      },
      {
        title: t('workspace.auditPage.columns.actor'),
        dataIndex: 'actorName',
        key: 'actor',
        width: 220,
        render: (_actorName: string, record) => (
          <UserCell
            firstName={record.actorName}
            email={record.actorEmail}
            size="sm"
          />
        )
      },
      {
        title: t('workspace.auditPage.columns.action'),
        dataIndex: 'action',
        key: 'action',
        width: 180,
        render: (_action: string, record) => (
          <AuditActionTag action={getAuditEventCode(record)} maxWidth={165} />
        )
      },
      {
        title: t('workspace.auditPage.columns.description'),
        dataIndex: 'descriptionLabel',
        key: 'description',
        ellipsis: { showTitle: false },
        render: (description: string) =>
          description ? (
            <Tooltip placement="topLeft" title={description}>
              <Text style={{ fontSize: 12 }}>{description}</Text>
            </Tooltip>
          ) : (
            <Text type="secondary">—</Text>
          )
      },
      {
        title: t('workspace.auditPage.columns.severity'),
        dataIndex: 'severityValue',
        key: 'severity',
        width: 110,
        render: (severity: SeverityEnum | null) =>
          severity ? (
            <Tag
              color={SEVERITY_TAG_COLOR[severity]}
              style={{
                marginInlineEnd: 0,
                fontSize: 10,
                textTransform: 'capitalize'
              }}
            >
              {translateAuditSeverity(t, severity)}
            </Tag>
          ) : (
            <Text type="secondary">—</Text>
          )
      },
      {
        title: t('workspace.auditPage.columns.ip'),
        dataIndex: 'ipAddress',
        key: 'ip',
        width: 140,
        render: (ip: string | null | undefined) =>
          ip ? (
            <Text code style={{ fontSize: 11 }}>
              {ip}
            </Text>
          ) : (
            <Text type="secondary">—</Text>
          )
      }
    ],
    [t]
  )

  const filtersDirty =
    searchTerm !== '' ||
    severityFilter !== 'all' ||
    actorTypeFilter !== '' ||
    actorIdFilter !== '' ||
    entityIdFilter !== '' ||
    eventTypeFilter !== '' ||
    serviceFilter !== '' ||
    fromFilter !== '' ||
    toFilter !== ''

  if (isError) {
    return <QueryErrorAlert onRetry={() => void refetch()} />
  }

  return (
    <>
      <div
        ref={tableTopRef}
        style={{ scrollMarginTop: TABLE_SCROLL_MARGIN_TOP }}
      >
        <ProTableContainer<DecoratedEvent>
          tableIdentifier="workspace-audit-events"
          searchPlaceholder={t('workspace.auditPage.searchPlaceholder')}
          rowKey="id"
          columns={columns}
          data={{ count: total, results: rows }}
          loading={{
            spinning: isLoading || isFetching,
            delay: 200
          }}
          onChange={(pagination) => {
            if (
              (pagination.current != null && pagination.current !== page) ||
              (pagination.pageSize != null && pagination.pageSize !== perPage)
            ) {
              scrollToTableTop()
            }
          }}
          showSorterTooltip={false}
          onRow={(record) => {
            const openDetails = () =>
              navigate(
                paths.workspaces.auditLogDetails(
                  String(workspaceId),
                  record.id
                ),
                {
                  state: {
                    auditListPath: `${location.pathname}${location.search}`
                  }
                }
              )

            return {
              'aria-label': t('workspace.auditPage.openDetails', {
                event: record.descriptionLabel
              }),
              role: 'button',
              tabIndex: 0,
              onClick: openDetails,
              onKeyDown: (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  openDetails()
                }
              }
            }
          }}
          scroll={{ x: 1230, scrollToFirstRowOnChange: true }}
          locale={{
            emptyText: filtersDirty
              ? t('workspace.auditPage.noMatches')
              : t('workspace.auditPage.empty')
          }}
          cardTitle={header ?? viewSelector}
          cardExtra={
            <Flex align="center" gap={8} style={{ flexShrink: 0 }}>
              {header ? viewSelector : null}
              <Text
                type="secondary"
                style={{ fontSize: 11, whiteSpace: 'nowrap' }}
              >
                {t('workspace.auditPage.toolbar.events', { count: total })}
              </Text>
              <Tooltip title={t('workspace.auditPage.toolbar.refresh')}>
                <Button
                  size="small"
                  type="text"
                  icon={<RotateCw size={14} />}
                  onClick={() => void refetch()}
                  loading={isFetching}
                  aria-label={t('workspace.auditPage.toolbar.refresh')}
                />
              </Tooltip>
              {canExportAudit ? (
                <>
                  <Divider orientation="vertical" style={{ margin: 0 }} />
                  <Button
                    size="small"
                    icon={<Download size={14} />}
                    onClick={() => setExportOpen(true)}
                  >
                    {t('workspace.auditPage.actions.export')}
                  </Button>
                </>
              ) : null}
            </Flex>
          }
          leftChildren={
            <>
              <FilterSelect
                value={eventTypeFilter}
                options={eventType.options}
                onSelect={(value) => setFilter({ event_type: value || null })}
                filterKey="event_type"
                onSearch={eventType.onSearch}
                placeholder={t('workspace.auditPage.filters.allActions')}
                width={FILTER_CONTROL_WIDTH}
              />
              <FilterSelect
                value={actorIdFilter}
                options={actor.options}
                onSelect={(value) => setFilter({ actor_id: value || null })}
                filterKey="actor_id"
                onSearch={actor.onSearch}
                loading={actor.loading}
                placeholder={t('workspace.auditPage.filters.allActors')}
                width={FILTER_CONTROL_WIDTH}
              />
              <MoreFilters
                filterActiveCount={moreFiltersActiveCount}
                onResetMoreFilters={resetMoreFilters}
                buttonProps={{ size: 'small' }}
              >
                <MoreFilters.Col
                  title={t('workspace.auditPage.filters.allApplications')}
                >
                  <FilterSelect
                    value={serviceFilter}
                    options={service.options}
                    onSelect={(value) => setFilter({ service: value || null })}
                    filterKey="service"
                    onSearch={service.onSearch}
                    placeholder={t(
                      'workspace.auditPage.filters.allApplications'
                    )}
                    width="100%"
                  />
                </MoreFilters.Col>

                <MoreFilters.Col
                  title={t('workspace.auditPage.filters.allActorTypes')}
                >
                  <FilterSelect
                    value={actorTypeFilter}
                    options={actorType.options}
                    onSelect={handleActorTypeChange}
                    filterKey="actor_type"
                    onSearch={actorType.onSearch}
                    placeholder={t('workspace.auditPage.filters.allActorTypes')}
                    width="100%"
                  />
                </MoreFilters.Col>

                <MoreFilters.Col
                  title={t('workspace.auditPage.filters.allSeverities')}
                >
                  <Select<SeverityFilter>
                    size="small"
                    allowClear
                    placeholder={t('workspace.auditPage.filters.allSeverities')}
                    value={
                      severityFilter === 'all' ? undefined : severityFilter
                    }
                    onChange={(value) => setFilter({ severity: value ?? null })}
                    style={{ width: '100%' }}
                    options={[
                      {
                        value: SeverityEnum.LOW,
                        label: (
                          <BadgeText
                            text={translateAuditSeverity(t, SeverityEnum.LOW)}
                            color={SEVERITY_COLORS[SeverityEnum.LOW]}
                          />
                        )
                      },
                      {
                        value: SeverityEnum.MEDIUM,
                        label: (
                          <BadgeText
                            text={translateAuditSeverity(
                              t,
                              SeverityEnum.MEDIUM
                            )}
                            color={SEVERITY_COLORS[SeverityEnum.MEDIUM]}
                          />
                        )
                      },
                      {
                        value: SeverityEnum.HIGH,
                        label: (
                          <BadgeText
                            text={translateAuditSeverity(t, SeverityEnum.HIGH)}
                            color={SEVERITY_COLORS[SeverityEnum.HIGH]}
                          />
                        )
                      }
                    ]}
                  />
                </MoreFilters.Col>

                <MoreFilters.Col
                  title={t('workspace.auditPage.filters.entityId')}
                >
                  <SearchInput
                    queryParam="entity_id"
                    size="small"
                    style={{ width: '100%' }}
                    placeholder={t('workspace.auditPage.filters.entityId')}
                  />
                </MoreFilters.Col>

                <MoreFilters.Col
                  title={t('workspace.auditPage.dateRangeLabel')}
                >
                  <RangePicker
                    size="small"
                    style={{ width: '100%' }}
                    value={rangeValue}
                    onChange={(next) =>
                      handleRangeChange(
                        next as [Dayjs | null, Dayjs | null] | null
                      )
                    }
                    aria-label={t('workspace.auditPage.dateRangeLabel')}
                  />
                </MoreFilters.Col>
              </MoreFilters>

              <Flex flex="1 0 100%">
                <ActiveFilterTags
                  items={filterTagItems}
                  onClearAll={filtersDirty ? clearFilters : undefined}
                />
              </Flex>
            </>
          }
        />
      </div>

      <AuditExportModal
        key={exportOpen ? 'open' : 'closed'}
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        organizationId={workspaceId}
        defaultPeriod={rangeValue}
        viewExportsPath={paths.workspaces.auditExports(String(workspaceId))}
        filters={{
          ...(searchTerm ? { search: searchTerm } : {}),
          ...(actorTypeFilter ? { actor_type: actorTypeFilter } : {}),
          ...(actorIdFilter ? { actor_id: actorIdFilter } : {}),
          ...(entityIdFilter ? { entity_id: entityIdFilter } : {}),
          ...(eventTypeFilter ? { event_type: eventTypeFilter } : {}),
          ...(serviceFilter ? { service: serviceFilter } : {}),
          ...(severityFilter !== 'all' ? { severity: severityFilter } : {})
        }}
      />
    </>
  )
}

export const EventsPanel = (props: Props) => (
  <FilterLabelsProvider>
    <EventsPanelBase {...props} />
  </FilterLabelsProvider>
)
